import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react-router";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSettings } from "@/contexts/SettingsContext";
import { formatCurrency, formatDate } from "@/lib/formatters";
import { getAuthHeaders } from "@/lib/api";
import { setMetaDescription, setCanonicalUrl, setOpenGraphTags, setTwitterCardTags, setMetaRobots } from "@/lib/seo";
import { generateWebPageSchema, generateBreadcrumbListSchema, addSchemaToPage } from "@/lib/schema";
import { Pencil, Trash2, Info } from "lucide-react";
import { ClickableTooltip } from "@/components/ui/tooltip";

type Saving = {
  id: number;
  amount: number;
  category: string;
  date: string;
  goal_amount?: number;
  goal_date?: string;
};

export default function SavingsPage() {
  useEffect(() => {
    const title = "Savings - Munthly";
    const description = "Set savings goals and track your progress with Munthly's free budgeting tool. Plan for future expenses and build your savings effectively.";
    
    document.title = title;
    setMetaDescription(description);
    const canonicalCleanup = setCanonicalUrl("/savings");
    const ogCleanup = setOpenGraphTags(title, description, "/savings");
    const twitterCleanup = setTwitterCardTags(title, description);
    const robotsCleanup = setMetaRobots("noindex, nofollow"); // Protected page - don't index
    
    // Add schema for savings page
    const webPageSchema = generateWebPageSchema(
      "/savings",
      title,
      description
    );
    const breadcrumbSchema = generateBreadcrumbListSchema([
      { name: "Home", url: "/" },
      { name: "Dashboard", url: "/dashboard" },
      { name: "Savings", url: "/savings" }
    ]);
    
    const schemaCleanup = addSchemaToPage([webPageSchema, breadcrumbSchema]);
    
    return () => {
      canonicalCleanup();
      ogCleanup();
      twitterCleanup();
      robotsCleanup();
      schemaCleanup();
    };
  }, []);

  const [savings, setSavings] = useState<Saving[]>([]);

  // Form State
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [hasGoal, setHasGoal] = useState(false);
  const [goalAmount, setGoalAmount] = useState("");
  const [goalDate, setGoalDate] = useState("");

  // State for Editing
  const [editingId, setEditingId] = useState<number | null>(null);

  const { settings } = useSettings();
  const { getToken } = useAuth();

  useEffect(() => {
    fetchSavings();
  }, []);

  const fetchSavings = async () => {
    const res = await fetch("/api/v1/savings", {
      headers: await getAuthHeaders(getToken)
    });
    const data = await res.json();
    setSavings(data);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!category || !date) return;
    if (hasGoal && (!goalAmount || !goalDate)) return;
    if (!hasGoal && !amount) return;

    if (editingId) {
      // Update existing
      const res = await fetch(`/api/v1/savings/${editingId}`, {
        method: "PUT",
        headers: await getAuthHeaders(getToken),
        body: JSON.stringify({
          amount: hasGoal ? null : parseFloat(amount),
          category,
          date,
          goal_amount: hasGoal ? parseFloat(goalAmount) : null,
          goal_date: hasGoal ? goalDate : null
        }),
      });
      if (!res.ok) {
        alert("Failed to update saving");
        return;
      }
      setEditingId(null);
    } else {
      // Create new
      const res = await fetch("/api/v1/savings", {
        method: "POST",
        headers: await getAuthHeaders(getToken),
        body: JSON.stringify({
          amount: hasGoal ? null : parseFloat(amount),
          category,
          date,
          goal_amount: hasGoal ? parseFloat(goalAmount) : null,
          goal_date: hasGoal ? goalDate : null
        }),
      });
      if (!res.ok) {
        alert("Failed to add saving");
        return;
      }
    }

    // Reset form
    setAmount("");
    setCategory("");
    setDate(new Date().toISOString().split("T")[0]);
    setHasGoal(false);
    setGoalAmount("");
    setGoalDate("");
    fetchSavings();
  };

  const handleEdit = (saving: Saving) => {
    setEditingId(saving.id);
    setCategory(saving.category);
    setDate(saving.date);
    if (saving.goal_amount && saving.goal_date) {
      setHasGoal(true);
      setGoalAmount(saving.goal_amount.toString());
      setGoalDate(saving.goal_date);
      setAmount("");
    } else {
      setHasGoal(false);
      setAmount(saving.amount.toString());
      setGoalAmount("");
      setGoalDate("");
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setAmount("");
    setCategory("");
    setDate(new Date().toISOString().split("T")[0]);
    setHasGoal(false);
    setGoalAmount("");
    setGoalDate("");
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this saving?")) return;

    await fetch(`/api/v1/savings/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders()
    });
    fetchSavings();
  };

  // Calculate monthly amount if goal is set
  const calculatedMonthly = hasGoal && goalAmount && goalDate ? (() => {
    const startDate = new Date(date);
    const endDate = new Date(goalDate);
    const monthsDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                       (endDate.getMonth() - startDate.getMonth());
    return monthsDiff > 0 ? parseFloat(goalAmount) / monthsDiff : parseFloat(goalAmount);
  })() : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-heading text-2xl sm:text-3xl font-semibold tracking-tight">Savings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your savings goals and monthly savings</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle>{editingId ? "Edit Saving" : "Add New Saving"}</CardTitle>
            <ClickableTooltip
              content={
                <div>
                  <p>Set up regular monthly savings or create a savings goal. For goals, add the target amount and date, and Munthly will calculate the monthly amount needed. For regular savings, add the monthly amount you want to save.</p>
                </div>
              }
            >
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                aria-label="How savings work"
              >
                <Info className="w-4 h-4" />
              </button>
            </ClickableTooltip>
          </div>
          <CardDescription>
            Set up monthly savings or create a savings goal with a target amount and date.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="category">Label</Label>
                <Input
                  type="text"
                  id="category"
                  placeholder="e.g. Emergency fund, Investments, Pension"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">Start Date</Label>
                <Input
                  type="date"
                  id="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="hasGoal"
                    checked={hasGoal}
                    onChange={(e) => {
                      setHasGoal(e.target.checked);
                      if (!e.target.checked) {
                        setGoalAmount("");
                        setGoalDate("");
                      }
                    }}
                    className="w-4 h-4 rounded border-[var(--outline)]"
                  />
                  <Label htmlFor="hasGoal" className="cursor-pointer">Set savings goal</Label>
                </div>
              </div>
            </div>

            {hasGoal ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="goalAmount">Goal Amount</Label>
                  <Input
                    type="number"
                    id="goalAmount"
                    placeholder="0.00"
                    value={goalAmount}
                    onChange={(e) => setGoalAmount(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="goalDate">Target Date</Label>
                  <Input
                    type="date"
                    id="goalDate"
                    value={goalDate}
                    onChange={(e) => setGoalDate(e.target.value)}
                  />
                </div>

                {calculatedMonthly && (
                  <div className="sm:col-span-2 p-3 rounded-2xl bg-[var(--surface-container)]">
                    <p className="text-sm text-muted-foreground">Monthly saving amount:</p>
                    <p className="text-lg font-bold" style={{ color: 'var(--success)' }}>
                      {formatCurrency(calculatedMonthly, settings.currencySymbol)}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="amount">Monthly Amount</Label>
                <Input
                  type="number"
                  id="amount"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
            )}

            <div className="flex gap-2 justify-end pt-2">
              {editingId && (
                <Button type="button" variant="outline" onClick={handleCancel}>Cancel</Button>
              )}
              <Button type="submit" disabled={!category || !date || (hasGoal ? (!goalAmount || !goalDate) : !amount)}>
                {editingId ? "Update Saving" : "Add Saving"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Savings</CardTitle>
        </CardHeader>
        <CardContent>
          {savings.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No savings recorded yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {savings.map((saving) => (
                <div key={saving.id} className="flex justify-between items-center p-4 rounded-2xl bg-[var(--surface-container)] hover:bg-[var(--surface-container-high)] transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-base">{saving.category}</p>
                      {saving.goal_amount && saving.goal_date ? (
                        <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-2.5 py-1 rounded-full font-medium">Goal</span>
                      ) : (
                        <span className="text-xs bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-300 px-2.5 py-1 rounded-full font-medium">Monthly</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {saving.goal_amount && saving.goal_date
                        ? `Goal: ${formatCurrency(saving.goal_amount, settings.currencySymbol)} by ${formatDate(saving.goal_date, settings.dateFormat)}`
                        : `Started: ${formatDate(saving.date, settings.dateFormat)}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-lg" style={{ color: 'var(--success)' }}>
                      {formatCurrency(saving.amount, settings.currencySymbol)}
                    </span>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon-sm" onClick={() => handleEdit(saving)}>
                        <Pencil className="w-4 h-4" />
                        <span className="sr-only">Edit</span>
                      </Button>
                      <Button variant="ghost" size="icon-sm" onClick={() => handleDelete(saving.id)} className="text-destructive hover:text-destructive">
                        <Trash2 className="w-4 h-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

